import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { COMMON_HELP_POPUP_BUTTON_CLASSNAME } from '../../../constants/components';
import { DEFAULT_HELP_POPUP_TITLE } from '../../../constants/error';
import MicButton from '../../../trc/pages/instructions/MicButton';
import {
    MIC_FAILURE_TYPES,
    MIC_FAILURE_MESSAGE,
    MIC_FAILURE_MESSAGE_AR,
} from '../../../trc/pages/instructions/TROInstructionsConstants';
import LogoutButton from '../../error/LogoutButton';
import HelpPopup from './HelpPopup';
import { redirectToAR, getARUrl } from '../../../trc/troUtils';

const TROTroubleshootingLink = '/support_center/TRO_Mic_Troubleshooting.pdf';
const TROTroubleshootingLinkAR =
    'https://amplify.com/wp-content/uploads/2022/07/Placement_Mic_Troubleshooting_Amplify-Reading.pdf';

const MIC_TEST_ATTEMPT_FAILURE_TYPES = [
    MIC_FAILURE_TYPES.SECOND_MIC_TEST_FAILED,
    MIC_FAILURE_TYPES.THIRD_MIC_TEST_FAILED,
];

export const AR_BENCHMARK_THIRD_MIC_TEST_FAILED_MESSAGE = `We cannot hear the student well enough to complete the speech recognition assessment.
The student is being sent back to Boost Reading, and a test reset is now available in your Teacher Dashboard.
Please try the speech version again later in a quieter environment.`;

const getProblemDescription = (
    problemType,
    { isExternalURLSourcePresent, isBenchmarkModeEnabled } = {},
) => {
    if (isExternalURLSourcePresent) {
        if (isBenchmarkModeEnabled && problemType === MIC_FAILURE_TYPES.THIRD_MIC_TEST_FAILED) {
            return AR_BENCHMARK_THIRD_MIC_TEST_FAILED_MESSAGE;
        }

        return MIC_FAILURE_MESSAGE_AR[problemType];
    }

    return MIC_FAILURE_MESSAGE[problemType];
};

const renderHelpMessages = (problem, onRestart) => {
    if (problem === MIC_FAILURE_TYPES.ACCESS_DENIED) {
        return (
            <div>
                <div>
                    Please follow these{' '}
                    <a
                        className="link new-tab"
                        href={TROTroubleshootingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        steps to turn on the microphone.
                    </a>
                </div>
                <div>
                    Once microphone has been turned on, please
                    <br />
                    <a className="link" href="#" onClick={onRestart}>
                        restart the assessment from the beginning.
                    </a>
                </div>
            </div>
        );
    }

    if (problem === MIC_FAILURE_TYPES.ACCESS_FAILED) {
        return (
            <div>
                <div>
                    Something went wrong. Please
                    <br />
                    <a className="link" onClick={onRestart} href="#">
                        restart the assessment from the beginning.
                    </a>
                </div>
                <div>
                    For troubleshooting steps,{' '}
                    <a
                        className="link new-tab"
                        href={TROTroubleshootingLink}
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        click here.{' '}
                    </a>
                </div>
            </div>
        );
    }
};

const renderArHelpMessages = (problem, onRestart, trackTroError) => {
    const queryParams = {
        hasTroError: true,
        benchmarkMode: false,
    };

    const onContinueWithoutMicClick = async event => {
        event.preventDefault();
        await trackTroError(problem);
        window.location.href = getARUrl(queryParams);
    };

    if (problem === MIC_FAILURE_TYPES.ACCESS_DENIED) {
        return (
            <div>
                <div>
                    Follow these{' '}
                    <a
                        className="link new-tab"
                        href={TROTroubleshootingLinkAR}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        steps to turn on the microphone.
                    </a>
                </div>
                <div>
                    Once microphone has been turned on, please{' '}
                    <a className="link" href="#" onClick={onRestart}>
                        restart the assessment
                    </a>{' '}
                    from the beginning.
                </div>
                <div>
                    <div className="line">
                        <span>or</span>
                    </div>
                    Continue without microphone{' '}
                    <a className="link" href="#" onClick={e => onContinueWithoutMicClick(e)}>
                        here
                    </a>
                </div>
            </div>
        );
    }

    if (problem === MIC_FAILURE_TYPES.ACCESS_FAILED) {
        return (
            <div>
                <div>
                    Something went wrong. The assessment needs to{' '}
                    <a className="link" onClick={onRestart} href="#">
                        restart from the beginning.
                    </a>
                </div>
                <div>
                    For troubleshooting tips,{' '}
                    <a
                        className="link new-tab"
                        href={TROTroubleshootingLink}
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        click here.
                    </a>
                </div>
                <div>
                    <div className="line">
                        <span>or</span>
                    </div>
                    Continue without microphone{' '}
                    <a className="link" href="#" onClick={e => onContinueWithoutMicClick(e)}>
                        here
                    </a>
                </div>
            </div>
        );
    }
    return false;
};

const getHelpButtons = (
    problem,
    onTestRetry,
    onLogout,
    onArMicTestFailure,
    isExternalURLSourcePresent,
    isActionButtonDisabled,
) => {
    // AR specific buttons
    if (isExternalURLSourcePresent) {
        if (problem === MIC_FAILURE_TYPES.THIRD_MIC_TEST_FAILED) {
            return [
                <button
                    key="popup-mic-continue-button"
                    className="mic-continue-button"
                    disabled={isActionButtonDisabled}
                    onClick={onArMicTestFailure}
                >
                    Continue
                </button>,
            ];
        }
    }

    if (problem === MIC_FAILURE_TYPES.SECOND_MIC_TEST_FAILED) {
        return [
            <MicButton
                ariaLabel="Microphone test"
                text="Test the microphone"
                onClick={onTestRetry}
                disabled={isActionButtonDisabled}
                key="popup-mic-test-button"
            />,
        ];
    }

    if (
        problem === MIC_FAILURE_TYPES.THIRD_MIC_TEST_FAILED ||
        problem === MIC_FAILURE_TYPES.MIC_TEST_ERROR
    ) {
        return [
            <LogoutButton
                key="log-out"
                className={COMMON_HELP_POPUP_BUTTON_CLASSNAME}
                onClick={onLogout}
                isSecondary
            />,
        ];
    }
};

const handlePopupOnBenchmarkMode = async trackTroError => {
    const queryParams = {
        hasTroError: true,
        benchmarkMode: true,
    };
    await trackTroError('Mic Help Popup: TRO Benchmark Mode enabled');
    redirectToAR(queryParams);
};

const MicHelpPopup = ({
    onRestart,
    problem,
    onTestRetry,
    onLogout,
    onArMicTestFailure,
    isActionButtonDisabled,
    isExternalURLSourcePresent,
    isBenchmarkModeEnabled,
    trackTroError,
    trackFailedEnableMic,
}) => {
    // The gated AR mic-test modals (2nd/3rd failure) must render even in benchmark
    // mode; only the access/error popups keep the benchmark auto-redirect behavior.
    const isGatedArMicModal =
        isExternalURLSourcePresent && MIC_TEST_ATTEMPT_FAILURE_TYPES.includes(problem);

    const hasVerticalLayout =
        MIC_TEST_ATTEMPT_FAILURE_TYPES.includes(problem) || !isExternalURLSourcePresent;

    useEffect(() => {
        if (!isExternalURLSourcePresent) trackFailedEnableMic();
    }, []);

    useEffect(() => {
        if (isBenchmarkModeEnabled && !isGatedArMicModal) {
            handlePopupOnBenchmarkMode(trackTroError);
        }
    }, [isBenchmarkModeEnabled]);

    return !isBenchmarkModeEnabled || isGatedArMicModal ? (
        <HelpPopup
            className={isExternalURLSourcePresent ? 'ar' : ''}
            hasVerticalLayout={hasVerticalLayout}
            title={DEFAULT_HELP_POPUP_TITLE}
            problem={getProblemDescription(problem, {
                isExternalURLSourcePresent,
                isBenchmarkModeEnabled,
            })}
            buttons={getHelpButtons(
                problem,
                onTestRetry,
                onLogout,
                onArMicTestFailure,
                isExternalURLSourcePresent,
                isActionButtonDisabled,
            )}
        >
            {isExternalURLSourcePresent
                ? renderArHelpMessages(problem, onRestart, trackTroError)
                : renderHelpMessages(problem, onRestart)}
        </HelpPopup>
    ) : null;
};

MicHelpPopup.propTypes = {
    onRestart: PropTypes.func.isRequired,
    problem: PropTypes.string.isRequired,
    onTestRetry: PropTypes.func,
    onLogout: PropTypes.func,
    onArMicTestFailure: PropTypes.func,
    isActionButtonDisabled: PropTypes.bool,
    isExternalURLSourcePresent: PropTypes.bool.isRequired,
    isBenchmarkModeEnabled: PropTypes.bool.isRequired,
    trackTroError: PropTypes.func,
    trackFailedEnableMic: PropTypes.func,
};

MicHelpPopup.defaultProps = {
    onTestRetry: () => {},
    onLogout: () => {},
    onArMicTestFailure: () => {},
    trackTroError: () => {},
    trackFailedEnableMic: () => {},
    isActionButtonDisabled: false,
};

export default MicHelpPopup;
